import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { X, Send, Loader2, AlertTriangle, ExternalLink, Monitor, Smartphone, History } from "lucide-react";

/**
 * BlogEmailFlow — modal fullscreen para preparar e disparar o "NOVO NO BLOG".
 *
 * Fluxo:
 *  1. Abre → GET /admin/blog/{id}/email (defaults, HTML render, contagem, histórico)
 *  2. Edição de Assunto / Intro / Preheader → POST /admin/blog/{id}/email/render (live preview)
 *  3. Enviar teste → POST /admin/blog/{id}/email/test  (prefixo [TESTE], sem métricas)
 *  4. Enviar real → POST /admin/blog/{id}/email/send  (confirmação + duplicate-protection)
 *
 * Mobile: layout fixed inset-0 com header sticky, corpo scrollable e footer sticky
 * respeitando `visualViewport` para o botão nunca ficar tapado pelo teclado.
 */
export default function BlogEmailFlow({ post, onClose, onSent }) {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [form, setForm] = useState({ subject: "", intro: "", preheader: "" });
    const [html, setHtml] = useState("");
    const [rendering, setRendering] = useState(false);
    const [testEmail, setTestEmail] = useState("");
    const [sendingTest, setSendingTest] = useState(false);
    const [sending, setSending] = useState(false);
    const [previewDevice, setPreviewDevice] = useState("desktop"); // 'desktop' | 'mobile'
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [showHistory, setShowHistory] = useState(false);
    const renderTimer = useRef(null);

    // ─── Initial load ─────────────────────────────────────────────
    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        api.get(`/admin/blog/${post.id}/email`)
            .then((r) => {
                if (cancelled) return;
                setData(r.data);
                setForm({
                    subject: r.data.defaults.subject,
                    intro: r.data.defaults.intro,
                    preheader: r.data.defaults.preheader,
                });
                setHtml(r.data.defaults.html);
            })
            .catch((e) => {
                toast.error(e?.response?.data?.detail || "Não foi possível abrir o fluxo");
                onClose();
            })
            .finally(() => !cancelled && setLoading(false));
        return () => { cancelled = true; };
    }, [post.id, onClose]);

    // ─── Live preview (debounced) ────────────────────────────────
    useEffect(() => {
        if (!data) return;
        if (renderTimer.current) clearTimeout(renderTimer.current);
        renderTimer.current = setTimeout(async () => {
            setRendering(true);
            try {
                const { data: rendered } = await api.post(
                    `/admin/blog/${post.id}/email/render`,
                    form,
                );
                setHtml(rendered.html);
            } catch { /* silent — user keeps typing */ }
            finally { setRendering(false); }
        }, 400);
        return () => renderTimer.current && clearTimeout(renderTimer.current);
    }, [form, data, post.id]);

    // ─── Visual viewport awareness (mobile keyboard) ──────────────
    // Reserves space at the bottom so the footer button is never hidden.
    const [vvBottomOffset, setVvBottomOffset] = useState(0);
    useEffect(() => {
        if (typeof window === "undefined" || !window.visualViewport) return;
        const vv = window.visualViewport;
        const onResize = () => {
            const offset = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
            setVvBottomOffset(offset);
        };
        vv.addEventListener("resize", onResize);
        vv.addEventListener("scroll", onResize);
        onResize();
        return () => {
            vv.removeEventListener("resize", onResize);
            vv.removeEventListener("scroll", onResize);
        };
    }, []);

    const doTest = async () => {
        const to = (testEmail || "").trim();
        if (!to || !to.includes("@")) return toast.error("Introduza um email válido");
        setSendingTest(true);
        try {
            await api.post(`/admin/blog/${post.id}/email/test`, { ...form, to });
            toast.success(`Teste enviado para ${to}`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Falha ao enviar teste");
        } finally {
            setSendingTest(false);
        }
    };

    const doSend = async (confirmDuplicate = false) => {
        setSending(true);
        try {
            await api.post(`/admin/blog/${post.id}/email/send`, {
                ...form, confirm_duplicate: confirmDuplicate,
            });
            toast.success("Envio iniciado. Vai começar a chegar às caixas de entrada.");
            setConfirmOpen(false);
            if (onSent) onSent();
            onClose();
        } catch (e) {
            const detail = e?.response?.data?.detail;
            if (detail?.code === "duplicate_send" && !confirmDuplicate) {
                // Backend refused because there's a prior send. Surface the message
                // in the confirm dialog so the admin can explicitly acknowledge.
                setConfirmOpen(true);
                toast.error("Este artigo já foi enviado. Confirme para reenviar.");
            } else {
                toast.error(typeof detail === "string" ? detail : (detail?.message || "Falha ao enviar"));
            }
        } finally {
            setSending(false);
        }
    };

    const recipientCount = data?.recipient_count ?? 0;
    const alreadySent = data?.already_sent;
    const cover = post.cover_id ? fileUrl(post.cover_id) : null;
    const previewWidth = previewDevice === "mobile" ? 375 : 720;

    return (
        <div className="fixed inset-0 z-50 bg-stone-950/60 flex flex-col" data-testid="blog-email-flow">
            {/* Sticky header */}
            <div className="flex-shrink-0 bg-white border-b border-stone-200 px-4 sm:px-6 py-3 flex items-center justify-between">
                <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--copper))] font-bold">Novo no Blog</div>
                    <h2 className="font-display text-base sm:text-lg font-bold text-stone-900 truncate max-w-[70vw]" title={post.title}>{post.title}</h2>
                </div>
                <button onClick={onClose} className="w-9 h-9 rounded-full border border-stone-300 flex items-center justify-center hover:bg-stone-50" aria-label="Fechar" data-testid="blog-email-close">
                    <X className="w-4 h-4" />
                </button>
            </div>

            {loading ? (
                <div className="flex-1 flex items-center justify-center text-stone-500 bg-stone-50"><Loader2 className="w-4 h-4 animate-spin mr-2" /> A carregar…</div>
            ) : (
                <div className="flex-1 min-h-0 overflow-y-auto bg-stone-50">
                    <div className="max-w-6xl mx-auto p-4 sm:p-6 grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)] gap-6">
                        {/* Left column: form */}
                        <div className="space-y-5">
                            {/* Article summary */}
                            <div className="bg-white rounded-xl border border-stone-200 p-4 flex gap-4" data-testid="blog-email-article-summary">
                                {cover && (
                                    <img src={cover} alt="" className="w-20 h-20 rounded-md object-cover flex-shrink-0" />
                                )}
                                <div className="min-w-0">
                                    {post.category && (
                                        <div className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--copper))] font-bold mb-1">— {post.category}</div>
                                    )}
                                    <div className="font-display font-bold text-stone-900 text-sm leading-snug break-words">{post.title}</div>
                                    <a href={data.defaults.article_url} target="_blank" rel="noopener noreferrer" className="text-xs text-stone-500 hover:text-[hsl(var(--copper))] inline-flex items-center gap-1 mt-1 break-all">
                                        {data.defaults.article_url}<ExternalLink className="w-3 h-3 flex-shrink-0" />
                                    </a>
                                </div>
                            </div>

                            {/* Duplicate warning */}
                            {alreadySent && (
                                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-900 flex items-start gap-3" data-testid="blog-email-duplicate-warning">
                                    <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                                    <div>
                                        Este artigo já foi enviado por email em <b>{formatDate(data.last_sent_at)}</b>. Pretende enviar novamente? Terá de confirmar no botão de envio.
                                    </div>
                                </div>
                            )}

                            {/* Recipient count */}
                            <div className="bg-white rounded-xl border border-stone-200 p-4 text-sm" data-testid="blog-email-recipients">
                                <div className="text-xs uppercase tracking-wider text-stone-500 font-bold mb-1">Destinatários</div>
                                <div className="text-stone-900">Todos os subscritores activos — <b data-testid="blog-email-recipient-count">{recipientCount}</b> {recipientCount === 1 ? "destinatário" : "destinatários"}</div>
                                <div className="text-xs text-stone-500 mt-1">Respeita unsubscribes, bounces e outros estados existentes.</div>
                            </div>

                            {/* Editable fields */}
                            <div className="bg-white rounded-xl border border-stone-200 p-4 space-y-4">
                                <label className="block">
                                    <span className="text-xs uppercase tracking-wider text-stone-500 font-bold block mb-1">Assunto do email</span>
                                    <input value={form.subject} onChange={(e) => setForm((s) => ({ ...s, subject: e.target.value }))}
                                        className="w-full border border-stone-300 rounded p-2 text-sm" maxLength={200}
                                        placeholder={post.title}
                                        data-testid="blog-email-subject" />
                                    <span className="text-[11px] text-stone-500 mt-1 block">Alterar aqui não modifica o artigo. Por defeito, título do artigo.</span>
                                </label>
                                <label className="block">
                                    <span className="text-xs uppercase tracking-wider text-stone-500 font-bold block mb-1">Preheader</span>
                                    <input value={form.preheader} onChange={(e) => setForm((s) => ({ ...s, preheader: e.target.value }))}
                                        className="w-full border border-stone-300 rounded p-2 text-sm" maxLength={200}
                                        placeholder="Aparece na inbox por baixo do assunto"
                                        data-testid="blog-email-preheader" />
                                    <span className="text-[11px] text-stone-500 mt-1 block">Se vazio, usa a intro. Fica oculto no corpo do email.</span>
                                </label>
                                <label className="block">
                                    <span className="text-xs uppercase tracking-wider text-stone-500 font-bold block mb-1">Texto de introdução</span>
                                    <textarea rows={4} value={form.intro} onChange={(e) => setForm((s) => ({ ...s, intro: e.target.value }))}
                                        className="w-full border border-stone-300 rounded p-2 text-sm" maxLength={600}
                                        placeholder="Frase curta que aparece antes do CTA. Por defeito, subtítulo do artigo."
                                        data-testid="blog-email-intro" />
                                </label>
                            </div>

                            {/* Test send */}
                            <div className="bg-white rounded-xl border border-stone-200 p-4 space-y-2">
                                <div className="text-xs uppercase tracking-wider text-stone-500 font-bold">Enviar teste</div>
                                <div className="flex gap-2">
                                    <input type="email" value={testEmail} onChange={(e) => setTestEmail(e.target.value)}
                                        className="flex-1 min-w-0 border border-stone-300 rounded p-2 text-sm"
                                        placeholder="ola@ohomemdaobra.pt" data-testid="blog-email-test-input" />
                                    <button onClick={doTest} disabled={sendingTest} className="px-4 py-2 border border-[hsl(var(--copper))] text-[hsl(var(--copper))] rounded-full text-sm flex items-center gap-2 disabled:opacity-50" data-testid="blog-email-test-send">
                                        {sendingTest ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Enviar teste
                                    </button>
                                </div>
                                <div className="text-[11px] text-stone-500">Prefixo <b>[TESTE]</b> automático. Não conta como campanha nem afecta o histórico.</div>
                            </div>

                            {/* History toggle */}
                            {data.history && data.history.length > 0 && (
                                <div className="bg-white rounded-xl border border-stone-200 p-4">
                                    <button onClick={() => setShowHistory((s) => !s)} className="w-full flex items-center justify-between text-sm" data-testid="blog-email-history-toggle">
                                        <span className="flex items-center gap-2 font-bold text-stone-900"><History className="w-4 h-4" /> Histórico ({data.history.length})</span>
                                        <span className="text-stone-500 text-xs">{showHistory ? "Ocultar" : "Ver"}</span>
                                    </button>
                                    {showHistory && (
                                        <div className="mt-3 space-y-2 text-sm" data-testid="blog-email-history-list">
                                            {data.history.map((h) => (
                                                <div key={h.id} className="flex flex-col gap-1.5 border-t border-stone-100 pt-2">
                                                    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                                                        <span className="text-stone-900 font-medium">{formatDate(h.sent_at || h.created_at)}</span>
                                                        <StatusBadge status={h.status} />
                                                        <span className="text-stone-500 text-xs">{h.sent_count || 0}/{h.recipient_count || 0} entregues</span>
                                                    </div>
                                                    <div className="text-xs text-stone-500 truncate">{h.subject}</div>
                                                    <BlogHistoryMetrics a={h.analytics || {}} />
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Right column: preview */}
                        <div className="lg:sticky lg:top-0">
                            <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
                                <div className="flex items-center justify-between px-3 py-2 border-b border-stone-200 bg-stone-50">
                                    <span className="text-xs uppercase tracking-wider text-stone-500 font-bold flex items-center gap-2">
                                        Pré-visualização {rendering && <Loader2 className="w-3 h-3 animate-spin" />}
                                    </span>
                                    <div className="flex gap-1">
                                        <button onClick={() => setPreviewDevice("desktop")} className={`px-2 py-1 rounded text-xs flex items-center gap-1 ${previewDevice === "desktop" ? "bg-stone-900 text-white" : "text-stone-600"}`} data-testid="blog-email-preview-desktop">
                                            <Monitor className="w-3 h-3" /> Desktop
                                        </button>
                                        <button onClick={() => setPreviewDevice("mobile")} className={`px-2 py-1 rounded text-xs flex items-center gap-1 ${previewDevice === "mobile" ? "bg-stone-900 text-white" : "text-stone-600"}`} data-testid="blog-email-preview-mobile">
                                            <Smartphone className="w-3 h-3" /> Mobile
                                        </button>
                                    </div>
                                </div>
                                <div className="p-4 flex justify-center bg-stone-100">
                                    <iframe
                                        title="Pré-visualização do email"
                                        srcDoc={html}
                                        style={{ width: previewWidth, maxWidth: "100%", height: "70vh", background: "#fff" }}
                                        className="border border-stone-200 rounded shadow-sm"
                                        data-testid="blog-email-preview-frame"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Sticky footer with keyboard awareness */}
            {!loading && (
                <div className="flex-shrink-0 bg-white border-t border-stone-200 px-4 sm:px-6 py-3"
                     style={{ paddingBottom: `calc(0.75rem + ${vvBottomOffset}px + env(safe-area-inset-bottom, 0px))` }}>
                    <div className="max-w-6xl mx-auto flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3">
                        <button onClick={onClose} className="text-sm text-stone-600 hover:text-stone-900 px-4 py-2" data-testid="blog-email-cancel">
                            Cancelar
                        </button>
                        <button
                            onClick={() => setConfirmOpen(true)}
                            disabled={sending || recipientCount === 0}
                            className="btn-copper !py-3 !px-6 justify-center disabled:opacity-50"
                            data-testid="blog-email-send-open">
                            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                            <span>Enviar para {recipientCount} {recipientCount === 1 ? "subscritor" : "subscritores"}</span>
                        </button>
                    </div>
                </div>
            )}

            {/* Confirm dialog */}
            {confirmOpen && (
                <div className="fixed inset-0 z-[60] bg-stone-950/70 flex items-center justify-center p-4" data-testid="blog-email-confirm-dialog">
                    <div className="bg-white rounded-xl max-w-md w-full p-6 space-y-4">
                        <div className="flex items-center gap-2">
                            <AlertTriangle className="w-5 h-5 text-[hsl(var(--copper))]" />
                            <h3 className="font-display text-lg font-bold text-stone-900">Confirmar envio</h3>
                        </div>
                        <p className="text-sm text-stone-600 leading-relaxed">
                            Vai disparar o email <b>{form.subject || post.title}</b> para <b>{recipientCount}</b> subscritores activos. Esta acção é irreversível.
                        </p>
                        {alreadySent && (
                            <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded p-3">
                                Este artigo já foi enviado em <b>{formatDate(data.last_sent_at)}</b>. Vai enviar <b>novamente</b>.
                            </p>
                        )}
                        <div className="flex gap-2 pt-2">
                            <button onClick={() => setConfirmOpen(false)} className="flex-1 border border-stone-300 rounded-full py-2 text-sm" data-testid="blog-email-confirm-cancel">Cancelar</button>
                            <button onClick={() => doSend(true)} disabled={sending} className="btn-copper flex-1 justify-center !py-2 disabled:opacity-50" data-testid="blog-email-confirm-send">
                                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                                Confirmar envio
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function formatDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString("pt-PT", { dateStyle: "medium", timeStyle: "short" });
    } catch { return iso; }
}

function StatusBadge({ status }) {
    const map = {
        sent: ["bg-emerald-100 text-emerald-800", "Enviado"],
        sent_with_errors: ["bg-amber-100 text-amber-800", "Parcial"],
        queued_partial: ["bg-amber-100 text-amber-800", "Parcial"],
        sending: ["bg-blue-100 text-blue-800", "A enviar"],
        failed: ["bg-red-100 text-red-800", "Falhou"],
    };
    const [cls, label] = map[status] || ["bg-stone-200 text-stone-700", status || "—"];
    return <span className={`text-[10px] uppercase tracking-wider rounded-full px-2 py-0.5 ${cls}`}>{label}</span>;
}


// Compact metrics row used inside the Blog article email history panel.
// Aberturas are flagged as "indicativas" — Apple Mail Privacy Protection
// and Gmail prefetch inflate opens without human interaction.
function BlogHistoryMetrics({ a }) {
    if (!a || typeof a !== "object") return null;
    const chip = (label, value, pct, cls, ind = false) => (
        <span className={`inline-flex items-baseline gap-1 text-[10px] rounded-full border px-2 py-0.5 ${cls}`}>
            <span className="uppercase tracking-widest text-[9px] font-mono">{label}{ind ? " (ind.)" : ""}</span>
            <b className="font-mono tabular-nums">{value || 0}</b>
            {typeof pct === "number" && <span className="opacity-70 font-mono tabular-nums">{pct.toFixed(1)}%</span>}
        </span>
    );
    return (
        <div className="flex flex-wrap gap-1.5" data-testid="blog-email-history-metrics">
            {chip("Entregues", a.delivered, a.delivered_pct, "bg-emerald-50 text-emerald-800 border-emerald-100")}
            {chip("Aberturas", a.opened, a.opened_pct, "bg-[hsl(var(--copper))]/10 text-[hsl(var(--copper))] border-[hsl(var(--copper))]/20", true)}
            {chip("Cliques", a.clicked, a.clicked_pct, "bg-[hsl(var(--copper))]/10 text-[hsl(var(--copper))] border-[hsl(var(--copper))]/20")}
            {a.bounced > 0 && chip("Bounces", a.bounced, a.bounced_pct, "bg-amber-50 text-amber-800 border-amber-200")}
            {a.complained > 0 && chip("Queixas", a.complained, a.complained_pct, "bg-red-50 text-red-700 border-red-100")}
            {a.unsubscribed > 0 && chip("Unsubs", a.unsubscribed, undefined, "bg-stone-100 text-stone-700 border-stone-200")}
        </div>
    );
}
